import { Typography } from '@mui/material'
import type { MRT_Row, MRT_TableInstance, MRT_TableState } from 'material-react-table'
import { getMRT_Rows, MRT_TableBodyRow as MrtTableBodyRow } from 'material-react-table'
import React, { createContext, useContext } from 'react'
import type { MrtRowData } from './mrtCollectionTypes'

const NO_PINNED_ROW_IDS: string[] = []
const EMPTY_MESSAGE_FALLBACK_WIDTH = 360

export const getBodyLayoutDeps = <T extends MrtRowData>(
  state: MRT_TableState<T>,
  columns: unknown,
  data: unknown,
): unknown[] => {
  const hasPinnedColumns = Boolean(state.columnPinning.left?.length || state.columnPinning.right?.length)
  return [
    columns,
    data,
    state.columnOrder,
    state.columnVisibility,
    state.columnPinning,
    hasPinnedColumns ? state.columnSizing : undefined,
    state.density,
    state.isLoading,
    state.showSkeletons,
    state.expanded,
    state.grouping,
    state.globalFilter,
    state.isFullScreen,
    state.draggingColumn,
    state.hoveredColumn,
    state.draggingRow,
    state.hoveredRow,
    state.editingCell,
    state.editingRow,
    state.creatingRow,
    state.actionCell,
  ]
}

export const MrtBodyLayoutContext = createContext<unknown[]>([])

type BodyRowProps<T extends MrtRowData> = {
  row: MRT_Row<T>
  staticRowIndex: number
  numRows: number
  table: MRT_TableInstance<T>
  layoutDeps: unknown[]
  isExpanded: boolean
}

const haveSameItems = (previous: unknown[], next: unknown[]) =>
  previous.length === next.length && previous.every((item, index) => item === next[index])

export const areBodyRowPropsEqual = (
  { layoutDeps: previousLayoutDeps, ...previous }: BodyRowProps<MrtRowData>,
  { layoutDeps: nextLayoutDeps, ...next }: BodyRowProps<MrtRowData>,
): boolean =>
  (Object.keys(next) as Array<keyof typeof next>).every(key => previous[key] === next[key]) &&
  haveSameItems(previousLayoutDeps, nextLayoutDeps)

const MemoBodyRow = React.memo(
  ({ row, staticRowIndex, numRows, table }: BodyRowProps<MrtRowData>) => (
    <MrtTableBodyRow
      row={row}
      staticRowIndex={staticRowIndex}
      numRows={numRows}
      table={table}
      pinnedRowIds={NO_PINNED_ROW_IDS}
    />
  ),
  areBodyRowPropsEqual,
) as <T extends MrtRowData>(props: BodyRowProps<T>) => React.ReactElement

const EmptyRow = <T extends MrtRowData>({ table }: { table: MRT_TableInstance<T> }): React.ReactElement => {
  const { columnFilters, globalFilter } = table.getState()
  const { localization } = table.options
  const paperWidth = table.refs.tablePaperRef.current?.clientWidth ?? EMPTY_MESSAGE_FALLBACK_WIDTH
  return (
    <tr style={{ display: 'grid' }}>
      <td colSpan={table.getVisibleLeafColumns().length} style={{ display: 'grid' }}>
        <Typography
          sx={{
            color: 'text.secondary',
            fontStyle: 'italic',
            py: 4,
            textAlign: 'center',
            width: '100%',
            maxWidth: `min(100vw, ${paperWidth}px)`,
          }}
        >
          {globalFilter || columnFilters.length ? localization.noResultsFound : localization.noRecordsToDisplay}
        </Typography>
      </td>
    </tr>
  )
}

export const MrtBodyRows = <T extends MrtRowData>({ table }: { table: MRT_TableInstance<T> }): React.ReactElement => {
  const layoutDeps = useContext(MrtBodyLayoutContext)
  const rows = getMRT_Rows(table)

  if (!rows.length) return <EmptyRow table={table} />

  return (
    <>
      {rows.map((row, staticRowIndex) => (
        <MemoBodyRow
          key={`${row.id}-${row.index}`}
          row={row}
          staticRowIndex={staticRowIndex}
          numRows={rows.length}
          table={table}
          layoutDeps={layoutDeps}
          isExpanded={row.getIsExpanded()}
        />
      ))}
    </>
  )
}
